import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import {
  FiPlus, FiEdit, FiTrash2, FiEye, FiEyeOff, FiUpload, FiX,
  FiFileText, FiPaperclip, FiVideo, FiBookOpen, FiSearch,
} from 'react-icons/fi';
import { guideService } from '../services/api';

const API_HOST = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api')
  .replace(/\/api\/?$/, '');
const absoluteUrl = (u) => {
  if (!u) return '';
  if (u.startsWith('http://') || u.startsWith('https://') || u.startsWith('data:')) return u;
  return `${API_HOST}${u.startsWith('/') ? '' : '/'}${u}`;
};

const BLANK = {
  section: 'General', title: '', body: '', youtubeUrl: '',
  order: 0, isActive: true, attachments: [],
};

// Common section presets — merchant sees these grouped in their guide
// browser. Reseller can type a custom value too.
const SECTION_SUGGESTIONS = [
  'Getting Started', 'POS', 'Products', 'Inventory', 'Sales',
  'Customers', 'Reports', 'Settings', 'Online Shop', 'Advanced', 'General',
];

const HowToUse = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [search, setSearch] = useState('');
  const [sectionFilter, setSectionFilter] = useState('all');

  const fetchAll = async () => {
    setLoading(true);
    try {
      const res = await guideService.list();
      setItems(res.data?.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to load guides');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchAll(); }, []);

  const sections = useMemo(() => {
    const uniq = new Set(items.map(i => i.section || 'General'));
    return ['all', ...Array.from(uniq).sort()];
  }, [items]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter(i => {
      if (sectionFilter !== 'all' && (i.section || 'General') !== sectionFilter) return false;
      if (!q) return true;
      return (i.title || '').toLowerCase().includes(q)
          || (i.body  || '').toLowerCase().includes(q)
          || (i.section || '').toLowerCase().includes(q);
    });
  }, [items, search, sectionFilter]);

  const openCreate = () => { setEditId(null); setForm(BLANK); setShowModal(true); };
  const openEdit = (item) => {
    setEditId(item._id);
    setForm({
      section:     item.section     || 'General',
      title:       item.title       || '',
      body:        item.body        || '',
      youtubeUrl:  item.youtubeUrl  || '',
      order:       item.order       || 0,
      isActive:    item.isActive !== false,
      attachments: Array.isArray(item.attachments) ? item.attachments : [],
    });
    setShowModal(true);
  };
  const closeModal = () => { setShowModal(false); setEditId(null); setForm(BLANK); };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    // Client-side size guard so an oversized file gets an immediate,
    // human-readable error rather than a raw 413 from nginx. Server enforces
    // the same 50 MB cap in middleware/upload.js.
    const MAX_MB = 50;
    const tooBig = files.find(f => f.size > MAX_MB * 1024 * 1024);
    if (tooBig) {
      const sizeMb = (tooBig.size / (1024 * 1024)).toFixed(1);
      toast.error(`"${tooBig.name}" is ${sizeMb} MB — max ${MAX_MB} MB per file.`);
      e.target.value = '';
      return;
    }

    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const res = await guideService.uploadFile(file);
        const d = res.data?.data;
        if (d?.url) uploaded.push({ url: d.url, type: d.type || 'other', filename: d.filename, size: d.size });
      }
      setForm(f => ({ ...f, attachments: [...(f.attachments || []), ...uploaded] }));
      toast.success(`${uploaded.length} file(s) uploaded`);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };
  const removeAttachment = (idx) => {
    setForm(f => ({ ...f, attachments: f.attachments.filter((_, i) => i !== idx) }));
  };

  const handleSave = async () => {
    if (!form.title.trim()) { toast.warning('Title is required'); return; }
    setSaving(true);
    try {
      if (editId) {
        await guideService.update(editId, form);
        toast.success('Guide updated');
      } else {
        await guideService.create(form);
        toast.success('Guide published');
      }
      closeModal();
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (item) => {
    try {
      await guideService.toggleActive(item._id);
      toast.success(item.isActive ? 'Guide hidden' : 'Guide is now visible');
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to toggle');
    }
  };
  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await guideService.remove(confirmDelete._id);
      toast.success('Guide deleted');
      setConfirmDelete(null);
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Delete failed');
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FiBookOpen /> How to Use
          </h1>
          <p className="text-sm text-gray-600 mt-1">Publish tutorials and guides for your merchants. They appear in the Help menu inside their POS.</p>
        </div>
        <button
          onClick={openCreate}
          className="inline-flex items-center px-4 py-2 bg-orange-500 text-white rounded-lg font-semibold hover:bg-orange-600 shadow-sm"
        >
          <FiPlus size={16} className="mr-2" /> New Guide
        </button>
      </div>

      <div className="bg-white rounded-lg border p-3 mb-4 flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search guides by title, body, or section..."
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-200"
          />
        </div>
        <select
          value={sectionFilter}
          onChange={(e) => setSectionFilter(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white sm:w-56"
        >
          {sections.map(s => (
            <option key={s} value={s}>{s === 'all' ? 'All sections' : s}</option>
          ))}
        </select>
      </div>

      <div className="bg-white rounded-xl shadow">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-gray-400">
            <FiBookOpen size={40} className="mx-auto mb-3 opacity-50" />
            <p className="font-medium">No guides yet</p>
            <p className="text-sm mt-1">Click "New Guide" to publish your first tutorial.</p>
          </div>
        ) : (
          <ul className="divide-y">
            {filtered.map(item => (
              <li key={item._id} className="p-4 flex items-start gap-4 hover:bg-gray-50">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold bg-blue-100 text-blue-700">
                      {item.section || 'General'}
                    </span>
                    <h3 className="font-semibold text-gray-900">{item.title}</h3>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${item.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {item.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  {item.body && <p className="text-sm text-gray-600 mt-1 whitespace-pre-line line-clamp-2">{item.body}</p>}
                  <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-gray-500">
                    <span>{new Date(item.createdAt).toLocaleDateString()}</span>
                    {item.youtubeUrl && (
                      <span className="inline-flex items-center gap-1 text-red-600"><FiVideo size={11} /> Video</span>
                    )}
                    {item.attachments?.length > 0 && (
                      <span className="inline-flex items-center gap-1">
                        <FiPaperclip size={11} /> {item.attachments.length} file{item.attachments.length !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => handleToggle(item)} title={item.isActive ? 'Hide from merchants' : 'Show to merchants'} className={`p-2 rounded ${item.isActive ? 'text-green-600 hover:bg-green-50' : 'text-gray-400 hover:bg-gray-100'}`}>
                    {item.isActive ? <FiEye size={16} /> : <FiEyeOff size={16} />}
                  </button>
                  <button onClick={() => openEdit(item)} title="Edit" className="p-2 text-blue-500 hover:bg-blue-50 rounded">
                    <FiEdit size={16} />
                  </button>
                  <button onClick={() => setConfirmDelete(item)} title="Delete" className="p-2 text-red-400 hover:bg-red-50 rounded">
                    <FiTrash2 size={16} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Editor modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={closeModal}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center px-6 py-4 border-b">
              <h2 className="text-lg font-bold">{editId ? 'Edit Guide' : 'Publish Guide'}</h2>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-600"><FiX size={20} /></button>
            </div>

            <div className="p-6 space-y-4 overflow-auto">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Section</label>
                  <input
                    type="text"
                    list="section-suggestions"
                    value={form.section}
                    onChange={(e) => setForm(f => ({ ...f, section: e.target.value }))}
                    placeholder="e.g. POS, Reports, Settings"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-400 outline-none"
                  />
                  <datalist id="section-suggestions">
                    {SECTION_SUGGESTIONS.map(s => <option key={s} value={s} />)}
                  </datalist>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Order (lower = higher)</label>
                  <input
                    type="number"
                    value={form.order}
                    onChange={(e) => setForm(f => ({ ...f, order: Number(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-400 outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
                  placeholder="e.g. How to add a new product"
                  maxLength={200}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-400 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Body</label>
                <textarea
                  value={form.body}
                  onChange={(e) => setForm(f => ({ ...f, body: e.target.value }))}
                  rows={6}
                  placeholder="Step-by-step instructions. Line breaks are preserved."
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-400 outline-none resize-y"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">YouTube URL</label>
                <input
                  type="url"
                  value={form.youtubeUrl}
                  onChange={(e) => setForm(f => ({ ...f, youtubeUrl: e.target.value }))}
                  placeholder="https://www.youtube.com/watch?v=... or https://youtu.be/..."
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-orange-400 outline-none"
                />
                <p className="text-[11px] text-gray-500 mt-1">Optional tutorial video. Embedded inline on the merchant side.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Attachments (images / PDF)</label>
                <label className="flex items-center gap-2 px-3 py-2 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-orange-400 hover:bg-orange-50 transition text-sm text-gray-600">
                  <FiUpload size={16} />
                  <span>{uploading ? 'Uploading…' : 'Click to upload files'}</span>
                  <input
                    type="file"
                    multiple
                    accept="image/*,application/pdf"
                    onChange={handleFileUpload}
                    disabled={uploading}
                    className="hidden"
                  />
                </label>
                <p className="text-[11px] text-gray-500 mt-1">
                  Images (JPG, PNG, GIF, WebP) or PDF · max <strong>50 MB</strong> per file · you can select multiple at once
                </p>
                {form.attachments.length > 0 && (
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    {form.attachments.map((att, idx) => (
                      <div key={idx} className="flex items-center gap-2 p-2 border rounded-lg bg-gray-50">
                        {att.type === 'image' ? (
                          <img src={absoluteUrl(att.url)} alt="" className="w-10 h-10 object-cover rounded" />
                        ) : (
                          <div className="w-10 h-10 flex items-center justify-center bg-red-100 text-red-600 rounded"><FiFileText size={18} /></div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-medium truncate">{att.filename || att.url.split('/').pop()}</div>
                          <div className="text-[10px] text-gray-500 uppercase">{att.type}</div>
                        </div>
                        <button onClick={() => removeAttachment(idx)} className="text-red-400 hover:text-red-600 p-1"><FiX size={14} /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e) => setForm(f => ({ ...f, isActive: e.target.checked }))}
                  className="w-4 h-4 accent-orange-500"
                />
                <span>Publish immediately (visible to merchants in their Help menu)</span>
              </label>
            </div>

            <div className="flex justify-end gap-2 px-6 py-4 border-t bg-gray-50">
              <button onClick={closeModal} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100">Cancel</button>
              <button onClick={handleSave} disabled={saving} className="px-4 py-2 bg-orange-500 text-white rounded-lg font-semibold hover:bg-orange-600 disabled:opacity-50">
                {saving ? 'Saving…' : (editId ? 'Save changes' : 'Publish')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setConfirmDelete(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-2">Delete this guide?</h3>
            <p className="text-sm text-gray-600 mb-6">"{confirmDelete.title}" will be removed from every merchant's Help menu.</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmDelete(null)} className="px-4 py-2 border border-gray-300 rounded-lg">Cancel</button>
              <button onClick={handleDelete} className="px-4 py-2 bg-red-500 text-white rounded-lg font-semibold hover:bg-red-600">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HowToUse;
